import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Parent (guardian) API service. Covers parent role enablement, the child
 * invite flow, and guardian-gated reads of a child's data. All calls go
 * through fetchWithAuth so the bearer token / refresh handling is consistent
 * with the rest of the app.
 */
const base = `${DATA_SERVICE_BASE_URL}/api/parents`;
const inviteBase = `${DATA_SERVICE_BASE_URL}/api/parent-invites`;

export const ParentApi = {
  // ─── Parent role + child management ───────────────────────────────
  /** Self-assign the additive "parent" role. */
  becomeParent: async () =>
    fetchWithAuth(`${base}/me`, { method: 'POST' }),

  /** Invite a child by email. */
  inviteChild: async ({ email, firstName, lastName } = {}) =>
    fetchWithAuth(`${base}/me/children/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, firstName, lastName }),
    }),

  /** List actively-linked children (ChildDTO[]). */
  getChildren: async () =>
    fetchWithAuth(`${base}/me/children`, { method: 'GET' }),

  /** List pending outgoing invites (GuardianInviteDTO[]). */
  getPendingInvites: async () =>
    fetchWithAuth(`${base}/me/children/invites`, { method: 'GET' }),

  /** Revoke a pending invite. */
  revokeInvite: async (inviteId) =>
    fetchWithAuth(`${base}/me/children/invites/${inviteId}`, { method: 'DELETE' }),

  /** Unlink an actively-linked child. */
  unlinkChild: async (childUserId) =>
    fetchWithAuth(`${base}/me/children/${childUserId}`, { method: 'DELETE' }),

  // ─── Child-facing invite responses ────────────────────────────────
  /** Validate a pending invite token (preview). */
  validateInvite: async (token) =>
    fetchWithAuth(`${inviteBase}/${encodeURIComponent(token)}/validate`, { method: 'GET' }),

  /** Accept a parent invite. */
  acceptInvite: async (token) =>
    fetchWithAuth(`${inviteBase}/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }),

  /** Decline a parent invite. */
  declineInvite: async (token) =>
    fetchWithAuth(`${inviteBase}/decline`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }),

  // ─── Guardian-gated child reads ───────────────────────────────────
  /** A child's courses/endeavors (EndeavorDTO[]). */
  getChildCourses: async (childUserId) =>
    fetchWithAuth(`${base}/me/children/${childUserId}/courses`, { method: 'GET' }),

  /** A child's active enrollments (EnrollmentDTO[]). */
  getChildEnrollments: async (childUserId) =>
    fetchWithAuth(`${base}/me/children/${childUserId}/enrollments`, { method: 'GET' }),

  /** A child's per-assignment/per-event progress within a course. */
  getChildCourseProgress: async (childUserId, endeavorId) =>
    fetchWithAuth(`${base}/me/children/${childUserId}/courses/${endeavorId}/progress`, { method: 'GET' }),

  /** A child's assignments (paged). */
  getChildAssignments: async (childUserId, { endeavorId, completed, timeframe = 'all', page = 0, size = 50 } = {}) => {
    const params = new URLSearchParams();
    if (endeavorId != null) params.set('endeavorId', endeavorId);
    if (completed != null) params.set('completed', completed);
    if (timeframe) params.set('timeframe', timeframe);
    params.set('page', page);
    params.set('size', size);
    return fetchWithAuth(`${base}/me/children/${childUserId}/assignments?${params.toString()}`, { method: 'GET' });
  },

  /** A child's recent activity breakdown and daily timeline. */
  getChildActivity: async (childUserId, days = 30) =>
    fetchWithAuth(`${base}/me/children/${childUserId}/activity?days=${days}`, { method: 'GET' }),

  /** A child's learning profile summary. */
  getChildProfile: async (childUserId) =>
    fetchWithAuth(`${base}/me/children/${childUserId}/profile`, { method: 'GET' }),
};

export default ParentApi;
